// src/components/LocationAutocomplete.js
import React, { useEffect, useRef, useState } from 'react';

const MIN_QUERY_LENGTH = 3;
const FETCH_DELAY_MS = 350;

/**
 * Location autocomplete input backed by OpenStreetMap Nominatim search.
 * Emits display text through onChange and returns lat/lng via onSelect.
 */
function LocationAutocomplete({
  id = 'location',
  name = 'location',
  value,
  placeholder = 'Start typing an address...',
  disabled = false,
  required = false,
  onChange,
  onSelect,
  onBlur,
  onFocus,
  className = ''
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    if (!open) {
      return () => {};
    }

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setLoading(false);
      setFetchError(null);
      return;
    }

    setLoading(true);
    setFetchError(null);

    const controller = new AbortController();
    abortRef.current = controller;

    debounceRef.current = setTimeout(() => {
      fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(trimmed)}&addressdetails=1&limit=5`, {
        signal: controller.signal,
        headers: {
          'Accept-Language': 'en'
        }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Suggestion lookup failed with status ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          const nextSuggestions = Array.isArray(data)
            ? data
                .map((item) => {
                  const lat = parseFloat(item.lat);
                  const lng = parseFloat(item.lon);
                  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                    return null;
                  }
                  return {
                    id: item.place_id,
                    displayName: item.display_name,
                    lat,
                    lng
                  };
                })
                .filter(Boolean)
            : [];
          setSuggestions(nextSuggestions);
          setLoading(false);
        })
        .catch((err) => {
          if (err.name === 'AbortError') {
            return;
          }
          console.error('Location autocomplete error:', err);
          setFetchError('Unable to load suggestions right now.');
          setLoading(false);
        });
    }, FETCH_DELAY_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [query]);

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setQuery(nextValue);
    onChange?.(nextValue);
    if (!open) {
      setOpen(true);
    }
  };

  const handleSelect = (suggestion) => {
    setQuery(suggestion.displayName);
    setOpen(false);
    setSuggestions([]);
    onSelect?.(suggestion);
  };

  const handleInputFocus = (event) => {
    setOpen(true);
    onFocus?.(event);
  };

  const handleInputBlur = (event) => {
    onBlur?.(event);
  };

  return (
    <div ref={containerRef} className={`location-autocomplete ${className}`} style={{ position: 'relative' }}>
      <input
        id={id}
        name={name}
        type="text"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        autoComplete="off"
        style={{
          width: '100%',
          padding: '0.5rem',
          borderRadius: '4px',
          border: '1px solid #ddd'
        }}
      />

      {open && (suggestions.length > 0 || loading || fetchError) && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 10,
            background: '#fff',
            border: '1px solid #ddd',
            borderTop: 'none',
            maxHeight: '220px',
            overflowY: 'auto',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
          }}
        >
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelect(suggestion)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '0.5rem 0.75rem',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer'
              }}
            >
              {suggestion.displayName}
            </button>
          ))}

          {loading && (
            <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: '#666' }}>
              Searching suggestions...
            </div>
          )}

          {fetchError && !loading && (
            <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: '#b94a48' }}>
              {fetchError}
            </div>
          )}

          {!loading && !fetchError && suggestions.length === 0 && query.trim().length >= MIN_QUERY_LENGTH && (
            <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: '#666' }}>
              No results found. Try refining the address.
            </div>
          )}
        </div>
      )}

      <small style={{ display: 'block', marginTop: '0.35rem', color: '#666' }}>
        Start typing to see suggested addresses (min {MIN_QUERY_LENGTH} characters).
      </small>
    </div>
  );
}

export default LocationAutocomplete;
